import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

export interface CollapsibleSectionProps {
  id?: string;
  title: string | React.ReactNode;
  subtitle?: string;
  icon?: React.ReactNode;
  badge?: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  variant?: 'default' | 'card' | 'ghost' | 'glass';
  headerRight?: React.ReactNode;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  id,
  title,
  subtitle,
  icon,
  badge,
  isOpen,
  onToggle,
  children,
  className = '',
  headerClassName = '',
  contentClassName = '',
  variant = 'card',
  headerRight
}) => {
  const getContainerStyle = () => {
    switch (variant) {
      case 'glass':
        return 'bg-white/[0.03] backdrop-blur-xl border border-white/10 rounded-2xl shadow-xl overflow-hidden';
      case 'card':
        return 'bg-slate-900/80 border border-slate-800 rounded-2xl overflow-hidden shadow-lg hover:border-slate-700/80 transition-colors';
      case 'ghost':
        return 'border-b border-white/10 py-3';
      case 'default':
      default:
        return 'bg-black/40 border border-white/10 rounded-xl overflow-hidden';
    }
  };

  return (
    <div className={`${getContainerStyle()} ${className}`}>
      <button
        type="button"
        id={id ? `${id}-header` : undefined}
        aria-expanded={isOpen}
        aria-controls={id ? `${id}-content` : undefined}
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-5 py-4 text-left transition-all group select-none ${
          isOpen ? 'bg-white/[0.04]' : 'hover:bg-white/[0.02]'
        } ${headerClassName}`}
      >
        <div className="flex items-center gap-3.5 min-w-0 pr-4">
          {icon && (
            <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 group-hover:scale-105 transition-transform flex-shrink-0">
              {icon}
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {typeof title === 'string' ? (
                <h3 className="font-semibold text-white text-base tracking-tight group-hover:text-emerald-400 transition-colors">
                  {title}
                </h3>
              ) : (
                title
              )}
              {badge && <span className="flex-shrink-0">{badge}</span>}
            </div>
            {subtitle && (
              <p className="text-xs text-white/50 mt-0.5 font-normal truncate">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          {headerRight}
          <div className={`p-1.5 rounded-lg bg-white/5 border border-white/10 text-white/60 group-hover:text-white group-hover:bg-white/10 transition-all ${
            isOpen ? 'rotate-180 bg-white/10 text-emerald-400' : ''
          }`}>
            <ChevronDown className="w-4 h-4 transition-transform duration-300" />
          </div>
        </div>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            id={id ? `${id}-content` : undefined}
            role="region"
            aria-labelledby={id ? `${id}-header` : undefined}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="overflow-hidden"
          >
            <div className={`px-5 py-5 border-t border-white/5 ${contentClassName}`}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
